import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';

import { OfferEntryData, GridRow, DataRow } from '../../../core/models/offerEntryData';

import {OfferEntryDataService} from '../../../core/services/offer-entry-data.service';

import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';

import { MeterUsageDataService } from '../../../core/services/meter-usage-data.service';
import { ChartUpdateService } from '../../../core/services/chart-update.service';
import {SingleDataLine, MeterData, Point, DataSeries} from './meterData';

import { Chart } from 'chart.js';
import {SpinnerService} from '../../../core/services/spinner.service';
import { forEach } from '@angular/router/src/utils/collection';
import {UtilsService} from '../../../core/services/utils.service';

const numberWithCommas = (x) => {
    var num: number = parseFloat(x.toString());
    var numberPart = Math.round(num).toString();
    var pretty = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return pretty;
}

@Component({
    selector: 'app-chart',
    templateUrl: './chart.component.html',
    styleUrls: ['./chart.component.css']
})
export class ChartComponent implements OnInit {
    debugVerbose: boolean = false;
    chart: Chart;
    canvas: HTMLCanvasElement;
    parent: HTMLElement;
    offerEntryData: OfferEntryData;
    meterUsageDataArray: MeterData[] = [];
    summedLines: DataSeries[] = null;
    willNeedRedraw: boolean = false;

    offerEntryDataSubscription: Subscription;
    meterUsageDataSubscription: Subscription;

    constructor(
        private offerEntryDataService: OfferEntryDataService,
        private chartUpdateService: ChartUpdateService,
        private meterUsageDataService: MeterUsageDataService,
        private spinnerService: SpinnerService,
        private utilsService: UtilsService
    ) { }

    createChart(): void {
        if (this.chart) {  // fix for tooltip bug (flickering/showing old charts when mouse over chart)
            this.chart.destroy();
        }
        this.parent = document.getElementById('chartParent');
        this.canvas = <HTMLCanvasElement>document.getElementById('canvas');
        const ctx = this.canvas.getContext('2d');

        this.chart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: [],
                datasets: []
            },
            options: {
                tooltips: {
                    enabled: true,
                    // mode: 'index',
                    // intersect: true,
                    callbacks: {
                        title: function (tooltipItem, data) {
                            if (data.datasets[tooltipItem[0].datasetIndex].steppedLine) {
                                const value = data.labels[tooltipItem[0].index];
                                return value;
                            } else {
                                return tooltipItem[0].xLabel;
                            }
                        },
                        label: function (tooltipItem, data) {
                            const value = tooltipItem.yLabel;
                            return data.datasets[tooltipItem.datasetIndex].label + ': ' + numberWithCommas(value) + 'kW';
                        }
                    } // end callbacks:
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 10,
                        bottom: 10
                    }
                },
                hover: { mode: null },
                responsive: true,
                maintainAspectRatio: false,
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        filter: (legendItem, chartData) => {
                            if (chartData.datasets[legendItem.datasetIndex].type === 'line') {
                                return true;  // only show legends for chart 'lines' as the bars are controlled by clicking the grid rows which act as legends for it
                            }
                            return false;
                            // return true or false based on legendItem's datasetIndex (legendItem.datasetIndex)
                        }
                    }
                },
                scales: {
                    xAxes: [
                        {
                            id: 'intervals',
                            categoryPercentage: 1.0,
                            barPercentage: 0.97,
                            display: true,
                            stacked: true
                        },
                        {
                            id: 'time',
                            display: false,
                            stacked: false,
                            type: 'time',
                            time: {
                                tooltipFormat: "HH:mm",
                                min: this.offerEntryData.startTime.getTime(),
                                max: this.offerEntryData.endTime.getTime()
                            },
                            //    unit: 'hour',
                            //    stepSize: 1,
                            // },
                            // ticks: {
                            //    beginAtZero: false
                            // },
                            // gridLines: {
                            //    offsetGridLines: true
                            // }
                        }],
                    yAxes: [{
                        id: 'bars',
                        display: true,
                        // stacked: true,
                        ticks: {
                            beginAtZero: true,
                            callback: function (value, index, values) {
                                return numberWithCommas(value).padStart(7) + 'kW';
                            }
                        },
                    }]
                }
            }
        });
        Chart.controllers.LineNoOffset = Chart.controllers.line.extend({
            updateElement: function (point, index, reset) {
                Chart.controllers.line.prototype.updateElement.call(this, point, index, reset);
                const meta = this.getMeta();
                const xScale = this.getScaleForId(meta.xAxisID);
                point._model.x = xScale.getPixelForValue(undefined, index - 0.5);
            },
        });
    }

    setCollatedValuesToX0(dataSeries: DataSeries) {
        dataSeries.collatedSeries = [];
        let maxTime = 0;
        for (let i = 0; i < dataSeries.intervals.length; i++) {
            let time = dataSeries.intervals[i].getTime();
            if (time > maxTime) {
                maxTime = time;
            }
            dataSeries.collatedSeries.push({ x: time, y: 0 });
        }
        if (dataSeries.stepped) {
            // add fake last point as it doesn't draw past the start of the last cell, (uses offer data scale for x)
            if (this.debugVerbose) {
                this.utilsService.debugLog('max time' + maxTime);
            }
            const fakeEndTime = maxTime + dataSeries.intervals[1].getTime() - dataSeries.intervals[0].getTime();
            if (this.debugVerbose) {
                this.utilsService.debugLog('fake end time' + fakeEndTime);
            }
            dataSeries.collatedSeries.push({ x: fakeEndTime, y: 0 });
        }
    }

    initSummedSeriesCollation(): void {
        this.summedLines = [];
        const meterUsageData: MeterData = this.getCurrentMeterData();
        if (meterUsageData && meterUsageData.dataSeries) {
            for (const dataSeries of meterUsageData.dataSeries) {
                if (dataSeries.intervals.length > 1) { // note - ignores series that are just a single dot
                    this.setCollatedValuesToX0(dataSeries);
                }
                dataSeries.stepped = false; // use a spline for the meter data
                this.summedLines.push(dataSeries);
            }
        }
        const summedGridDataSeries: DataSeries[] = [];
        for (const gridRow of this.offerEntryData.gridRows) {
            for (const dataRow of gridRow.dataRows) {
                if (dataRow.isOnChart && dataRow.typeOnChart === 'summed-line') {
                    let newDataSeries: DataSeries = {
                        label: dataRow.lineCaption,
                        collatedSeries: [],
                        intervals: [],
                        stepped: true, // all lines stepped by default unless otherwise set
                        colour: dataRow.lineColour,
                        datalines: []
                    }
                    for (let i = 0; i < this.offerEntryData.intervalTimes.length; i++) {
                        newDataSeries.intervals.push(this.offerEntryData.intervalTimes[i]);
                    }
                    let exists = false;
                    for (const dataSeries of summedGridDataSeries) {
                        if (dataSeries.label === newDataSeries.label) {
                            newDataSeries = dataSeries;
                            exists = true;
                        }
                    }
                    if (!exists) {
                        summedGridDataSeries.push(newDataSeries);
                    }

                    const newSingleDataLine: SingleDataLine = {
                        id: gridRow.id,
                        dataValues: dataRow.dataValues
                    }

                    newDataSeries.datalines.push(newSingleDataLine);

                    if (newDataSeries.intervals.length > 1) { // note - ignores series that are just a single dot
                        this.setCollatedValuesToX0(newDataSeries);
                    }
                }
            }
        }
        for (const series of summedGridDataSeries) {
            this.summedLines.push(series);
        }
    }

    // just copy in the changed values, directly over the data so any references stay the same
    mergeMeterData(meterUsageData: MeterData, loadedMeterUsageData: MeterData) {
        if (meterUsageData.dataSeries.length === loadedMeterUsageData.dataSeries.length) {
            for (let d = 0; d < meterUsageData.dataSeries.length; d++) {
                for (let l = 0; l < meterUsageData.dataSeries[d].datalines.length; l++) {
                    for (let v = 0; v < meterUsageData.dataSeries[d].datalines[l].dataValues.length; v++) {
                        meterUsageData.dataSeries[d].datalines[l].dataValues[v] = loadedMeterUsageData.dataSeries[d].datalines[l].dataValues[v];
                    }
                }
            }
        } else {
            const newMeterUsageData: MeterData = {
                dataLoadTimestamp: loadedMeterUsageData.dataLoadTimestamp,
                dataSeries: loadedMeterUsageData.dataSeries,
                endTime: loadedMeterUsageData.endTime,
                startTime: loadedMeterUsageData.startTime,
                level: loadedMeterUsageData.level,
                loadedPortfolioID: loadedMeterUsageData.loadedPortfolioID,
                product: loadedMeterUsageData.product
            };
            this.meterUsageDataArray.splice(this.meterUsageDataArray.indexOf(meterUsageData), 1, newMeterUsageData);
            this.willNeedRedraw = true;
        }
    }

    getGridRow(gridRowId: string): GridRow {
        for (const gridRow of this.offerEntryData.gridRows) {
            if (gridRow.id === gridRowId) {
                return gridRow;
            }
        }

        return null;
    }

    sumAllLines() {
        if (this.summedLines) {
            for (const dataSeries of this.summedLines) {
                const unfoundGridRowIds: string[] = [];
                if (dataSeries.intervals.length > 0) {
                    for (let i = 0; i < dataSeries.intervals.length; i++) {
                        dataSeries.collatedSeries[i].y = null;
                    }
                    for (const dataline of dataSeries.datalines) {
                        const gridRow: GridRow = this.getGridRow(dataline.id);
                        if (gridRow) {
                            for (let i = 0; i < dataSeries.intervals.length; i++) {
                                if (gridRow.isVisibleOnChart) {
                                    if (dataline.dataValues[i]) {
                                        if (!dataSeries.collatedSeries[i].y) {
                                            dataSeries.collatedSeries[i].y = dataline.dataValues[i];
                                        } else {
                                            dataSeries.collatedSeries[i].y = dataSeries.collatedSeries[i].y + dataline.dataValues[i];
                                        }
                                    }
                                }
                            }
                        } else {
                            if (unfoundGridRowIds.indexOf(dataline.id) < 0) {
                                unfoundGridRowIds.push(dataline.id);
                            }
                        }
                    }

                    if (dataSeries.stepped) {
                        dataSeries.collatedSeries[dataSeries.intervals.length].y = dataSeries.collatedSeries[dataSeries.intervals.length - 1].y; // add fake last point to widen the graph to display nicely (at same level as real last point)
                    }
                }
                if (unfoundGridRowIds.length > 0) {
                    console.error('Could not find gridrow(s): ' + unfoundGridRowIds.join(', ') + ' in ' + dataSeries.label);
                }

            }
            if (this.debugVerbose) {
                this.utilsService.debugLog('Summed lines');
                this.utilsService.debugLog(this.summedLines);
            }
        }
    }

    createSeries(): void {
        // this.canvas.width = this.parent.offsetWidth;
        // this.canvas.height = this.parent.offsetHeight;

        // var times: Date[] = [];
        // this.utilsService.debugLog("this.offerEntryData.intervalTimes");
        // this.utilsService.debugLog(this.offerEntryData.intervalTimes);
        // this.offerEntryData.intervalTimes.forEach(time => {
        //    times.push(new Date(time.getTime() + 30 * 60000));
        // });
        // this.utilsService.debugLog("times");
        // this.utilsService.debugLog(times);
        // this.chart.data.labels = times;

        if (this.offerEntryData.intervalTimes.length > 0) {
            this.chart.data.labels = this.offerEntryData.columnHeaders;

            this.initSummedSeriesCollation();
            this.sumAllLines();

            // draw lines first (so they show over the top of the bars)
            for (const additionalSeries of this.offerEntryData.additionalChartSeries) {
                const data: any = [];
                for (let i = 0; i < additionalSeries.xData.length && i < additionalSeries.yData.length; i++) {
                    const plotPoint = { x: additionalSeries.xData[i].getTime(), y: additionalSeries.yData[i] };
                    data.push(plotPoint);
                }
            }

            if (this.summedLines) {
                for (const dataSeries of this.summedLines) {
                    if (dataSeries.intervals.length > 0) {
                        this.chart.data.datasets.push({
                            data: dataSeries.collatedSeries,
                            label: dataSeries.label,
                            type: 'line',
                            steppedLine: dataSeries.stepped,
                            fill: false,
                            backgroundColor: dataSeries.colour,
                            borderColor: dataSeries.colour,
                            borderWidth: 2,
                            borderDash: (dataSeries.colour === '#000000') ? [5, 5] : [0, 0],
                            pointRadius: (dataSeries.stepped) ? 2 : 0,
                            pointHitRadius: 15,
                            xAxisID: 'time'
                        });
                    }
                }
            }

            for (const gridRow of this.offerEntryData.gridRows) {
                for (const dataRow of gridRow.dataRows) {
                    if (dataRow.isOnChart && dataRow.typeOnChart === 'line') {
                        let data: any = [];
                        for (let i = 0; i < this.offerEntryData.intervalTimes.length && i < dataRow.dataValues.length; i++) {
                            const plotPoint = { x: this.offerEntryData.intervalTimes[i].getTime(), y: dataRow.dataValues[i] };
                            data.push(plotPoint);
                        }
                        this.chart.data.datasets.push({
                            data: data,
                            'label': gridRow.name,
                            type: 'line',
                            steppedLine: true,
                            fill: false,
                            hidden: !gridRow.isVisibleOnChart,
                            backgroundColor: gridRow.chartColour,
                            borderColor: gridRow.chartColour,
                            borderWidth: 2,
                            xAxisID: 'time'
                        });
                    }
                }
            }
            for (let g = this.offerEntryData.sortedGridRows.length; g > 0; g--) {  // draw bars last and in same order as grid
                const gridRow = this.offerEntryData.sortedGridRows[g - 1];
                for (const dataRow of gridRow.dataRows) {
                    if (dataRow.isOnChart && dataRow.typeOnChart === 'bar') {
                        this.chart.data.datasets.push({
                            data: dataRow.dataValues,  // better to send actual datavalues so it stays bound (and will update) when grid changes
                            'label': gridRow.name,
                            type: 'bar',
                            hidden: !gridRow.isVisibleOnChart,
                            backgroundColor: gridRow.chartColour,
                            // borderWidth: 1,
                            // borderColor: "black",
                            stack: 'bar',
                            xAxisID: 'intervals',
                        });
                    }
                }
            }
        }

        if (this.chart) {
            this.chart.update();
        }
    }

    mergeOfferEntryData(loadedOfferEntryData: OfferEntryData): void {
        for (let g = 0; g < this.offerEntryData.gridRows.length; g++) {
            for (let d = 0; d < this.offerEntryData.gridRows[g].dataRows.length; d++) {
                for (let v = 0; v < this.offerEntryData.columnHeaders.length; v++) {
                    //if (!(this.cellSelection.isCellSelected(this.offerEntryData.gridRows[g].id, d, v) && this.cellSelection.isEdited())) {  // don't overwrite edits in progress
                        this.offerEntryData.gridRows[g].dataRows[d].dataValues[v] = loadedOfferEntryData.gridRows[g].dataRows[d].dataValues[v];
                    //}
                }
            }
        }

        if (this.chart) {
            this.chart.update();
        }
    }

    areDatesSame(date1: Date, date2: Date): boolean {
        if (date1 && date2) {
            if (date1.toDateString() === date2.toDateString()) {
                return true;
            } else {
                return false;
            }
        } else if (!date1 && !date2) {
            return true;
        } else {
            return false;
        }
    }

    getOfferEntryData(): void {
        this.offerEntryDataSubscription = this.offerEntryDataService.getOfferEntryData()
            .subscribe(loadedOfferEntryData => {
                if (loadedOfferEntryData) {
                    // this.utilsService.debugLog("Comparing");
                    // this.utilsService.debugLog(this.offerEntryData);
                    // this.utilsService.debugLog(offerEntryData);
                    if (this.offerEntryData
                        && this.arePortfoliosSame(this.offerEntryData.loadedPortfolioID, loadedOfferEntryData.loadedPortfolioID)
                        && this.offerEntryData.level === loadedOfferEntryData.level
                        && this.offerEntryData.product === loadedOfferEntryData.product
                        && this.offerEntryData.gridRows.length === loadedOfferEntryData.gridRows.length
                        && this.areDatesSame(this.offerEntryData.startTime, loadedOfferEntryData.startTime)
                        && this.areDatesSame(this.offerEntryData.endTime, loadedOfferEntryData.endTime)
                    ) {
                        if (this.debugVerbose) {
                            this.utilsService.debugLog('Merge offer data handled by grid');
                        }
                        //this.mergeOfferEntryData(loadedOfferEntryData);
                        //this.sumAllLines();
                    } else { // reload
                        if (this.debugVerbose) {
                            this.utilsService.debugLog('Reload offer data');
                        }
                        this.offerEntryData = loadedOfferEntryData;
                        this.offerEntryData.sortedGridRows = this.offerEntryData.gridRows;
                        this.sumAllLines();
                        this.redrawChart();
                    }
                }
            },
            error => {
                this.spinnerService.hide('meterSpinner');
                this.utilsService.debugLog('Could not load data for grid');
            });
    }

    redrawChart() {
        if (this.offerEntryData && this.offerEntryData.startTime) {
            const meterUsageData: MeterData = this.getCurrentMeterData();
            if (!meterUsageData) {
                if (this.debugVerbose) {
                    this.utilsService.debugLog('No meter data for current offer');
                    this.utilsService.debugLog(this.offerEntryData);
                    this.utilsService.debugLog(this.meterUsageDataArray);
                }
                this.spinnerService.show('meterSpinner');
                this.willNeedRedraw = true;
            } else {
                if (this.debugVerbose) {
                    this.utilsService.debugLog('Redrawing chart');
                }
                this.spinnerService.hide('meterSpinner');
                this.willNeedRedraw = false;
                this.createChart();
                this.initSummedSeriesCollation();
                this.createSeries();
            }
        } else {
            this.spinnerService.show('meterSpinner');
            this.willNeedRedraw = true;
        }
    }

    arePortfoliosSame(p1, p2) {
        let p1t = p1;
        let p2t = p2;
        if (p1t == null) {
            p1t = '';
        }
        if (p2t == null) {
            p2t = '';
        }
        if (p1t === '-1') {
            p1t = '';
        }
        if (p2t === '-1') {
            p2t = '';
        }
        if (p1t === p2t) {
            return true;
        } else {
            return false;
        }
    }

    doesMeterDataMatchOfferData(meterUsageData: MeterData) {
        if (!this.offerEntryData || !meterUsageData) {
            return false;
        } else if (this.arePortfoliosSame(this.offerEntryData.loadedPortfolioID, meterUsageData.loadedPortfolioID)
            && this.areDatesSame(this.offerEntryData.startTime, meterUsageData.startTime)
            && this.areDatesSame(this.offerEntryData.endTime, meterUsageData.endTime)
            && this.offerEntryData.level === meterUsageData.level
            && this.offerEntryData.product === meterUsageData.product) {
            return true;
        } else {
            return false;
        }
    }

    getCurrentMeterData(): MeterData {
        for (const meterData of this.meterUsageDataArray) {
            if (this.doesMeterDataMatchOfferData(meterData)) {
                return meterData;
            }
        }

        return null;
    }

    getMeterUsageData(): void {
        this.meterUsageDataSubscription = this.meterUsageDataService.getMeterDataArray()
            .subscribe(loadedMeterUsageDataArray => {
                if (loadedMeterUsageDataArray) {
                    let needRedraw = true;
                    for (const loadedMeterUsageData of loadedMeterUsageDataArray) {
                        let found = false;
                        for (const meterUsageData of this.meterUsageDataArray) {
                            if (this.arePortfoliosSame(meterUsageData.loadedPortfolioID, loadedMeterUsageData.loadedPortfolioID)
                                && meterUsageData.level === loadedMeterUsageData.level
                                && meterUsageData.product === loadedMeterUsageData.product
                                && this.areDatesSame(meterUsageData.startTime, loadedMeterUsageData.startTime)
                                && this.areDatesSame(meterUsageData.endTime, loadedMeterUsageData.endTime)
                            ) {
                                found = true;
                                if (meterUsageData.dataLoadTimestamp !== loadedMeterUsageData.dataLoadTimestamp) {
                                    if (this.debugVerbose) {
                                        this.utilsService.debugLog('Merge meter data ' + loadedMeterUsageData.loadedPortfolioID + ' on ' + loadedMeterUsageData.startTime);
                                    }

                                    this.mergeMeterData(meterUsageData, loadedMeterUsageData);
                                    if (this.doesMeterDataMatchOfferData(meterUsageData)) {
                                        this.sumAllLines();
                                        needRedraw = false;
                                    }
                                } else if (this.debugVerbose) {
                                    this.utilsService.debugLog('Skipped meter data ' + loadedMeterUsageData.loadedPortfolioID + ' on ' + loadedMeterUsageData.startTime + ' as timestamp is ' + loadedMeterUsageData.dataLoadTimestamp);
                                }
                            }
                        }
                        if (!found) {
                            if (this.debugVerbose) {
                                this.utilsService.debugLog('Added new meter data ' + loadedMeterUsageData.loadedPortfolioID + ' on ' + loadedMeterUsageData.startTime);
                            }

                            const newMeterUsageData: MeterData = {
                                dataLoadTimestamp: loadedMeterUsageData.dataLoadTimestamp,
                                dataSeries: loadedMeterUsageData.dataSeries,
                                endTime: loadedMeterUsageData.endTime,
                                startTime: loadedMeterUsageData.startTime,
                                level: loadedMeterUsageData.level,
                                loadedPortfolioID: loadedMeterUsageData.loadedPortfolioID,
                                product: loadedMeterUsageData.product
                            };
                            this.meterUsageDataArray.push(newMeterUsageData);
                            if (this.meterUsageDataArray.length > 3) {
                                this.meterUsageDataArray = this.meterUsageDataArray.sort((a, b) => b.dataLoadTimestamp - a.dataLoadTimestamp).slice(0, 3); // remove all but newest 3
                            }

                        }
                    }
                    if (needRedraw || this.willNeedRedraw) {
                        this.redrawChart();
                    }
                }
            });
    }

    ngOnInit() {
        this.getOfferEntryData();
        this.getMeterUsageData();
        this.chartUpdateService.currentNotification.subscribe(notification => {
            if (notification === 'showHideChange') {
                for (const gridRow of this.offerEntryData.gridRows) {
                    for (const graphRow of this.chart.data.datasets) {
                        if (gridRow.name === graphRow.label) {
                            graphRow.hidden = !gridRow.isVisibleOnChart;
                        }
                    }
                }
                this.sumAllLines();  // update meter display to exclude any rows now hidden
            }
            if (notification !== 'Startup') {
                if (this.chart) {
                    this.chart.update();
                }
            }
            if (notification.startsWith('SortChanged')) {
                const args = notification.split(',');
                this.offerEntryData.selectedSort.function(this.offerEntryData, parseInt(args[1]), parseInt(args[2]), (args[3] === 'TRUE') ? true : false);
                this.redrawChart();
            }
            if (notification.startsWith('reloadedGrid')) {
                this.utilsService.debugLog('reloaded grid data, updating chart');
                this.sumAllLines();
                this.chart.update();
            }
        })
    }

    public ngOnDestroy() {
        if (this.offerEntryDataSubscription) {
            this.offerEntryDataSubscription.unsubscribe();
        }
        if (this.meterUsageDataSubscription) {
            this.meterUsageDataSubscription.unsubscribe();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/chart/chart.component.ts